import { RoutingProfile } from '../../../../models/RoutingProfiles';
import { AgentAllocation } from '../../../../models/AgentsAllocation';
import { useRoutingProfileModal } from './useRoutingProfileModal';
import { SelectedAgentsList } from './SelectedAgentsList';
import { RoutingProfileSelector } from './RoutingProfileSelector';
import { setDefaultRoutingProfile } from '../../../../services/setDefaultRoutingProfile';
import { useState } from 'react';
import { Box, Button, Modal, ModalBody, ModalFooter } from '@coolblue-development/freddy';

interface DefaultRoutingProfileModalProps {
  open: boolean;
  onClose: () => void;
  selectedAgents: AgentAllocation[];
  allRoutingProfiles: RoutingProfile[];
  onSuccess: () => void;
  onError: (error: string) => void;
}

export const DefaultRoutingProfileModal = ({
  open,
  onClose,
  selectedAgents,
  allRoutingProfiles,
  onSuccess,
  onError
}: DefaultRoutingProfileModalProps): JSX.Element => {
  const {
    routingProfiles,
    selectedRoutingProfile,
    setSelectedRoutingProfile,
    isSelectionDisabled,
    resetState,
    isRoutingProfileSameForAllAgents
  } = useRoutingProfileModal(selectedAgents, allRoutingProfiles, open);

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const handleRoutingProfileChange = (value: string): void => {
    setSelectedRoutingProfile(value);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedRoutingProfile || selectedAgents.length === 0) {
      return;
    }

    try {
      setIsLoading(true);

      await setDefaultRoutingProfile({
        userIds: selectedAgents.map(agent => agent.id),
        routingProfileId: selectedRoutingProfile
      });

      onSuccess();
      handleClose();
    } catch (error) {
      onError(`Failed to set default routing profiles: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const hasError = !isRoutingProfileSameForAllAgents;

  const errorMessage =
    'Cannot change default routing profile because selected agents have different default routing profiles.';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Set default routing profile"
      size="large"
      translations={{ ariaLabelClose: 'Close' }}
      sx={{ overflow: 'visible', margin: 'auto' }}
      body={
        <ModalBody>
          <SelectedAgentsList selectedAgents={selectedAgents} />

          <RoutingProfileSelector
            selectedRoutingProfile={selectedRoutingProfile}
            onRoutingProfileChange={handleRoutingProfileChange}
            routingProfiles={routingProfiles}
            hasError={hasError}
            isDisabled={isSelectionDisabled || isLoading}
            errorMessage={hasError ? errorMessage : undefined}
          />
        </ModalBody>
      }
      footer={
        <ModalFooter>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={handleClose} disabled={isLoading} variant="secondary">
              Cancel
            </Button>

            <Button
              disabled={!selectedRoutingProfile || selectedAgents.length === 0 || isLoading}
              onClick={handleSubmit}
              loading={isLoading}
              variant="primary"
            >
              {isLoading ? 'Updating...' : 'Confirm'}
            </Button>
          </Box>
        </ModalFooter>
      }
    />
  );
};
